import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { scrollToSection } from '../hooks/useSmoothScroll';
import { 
  BookOpen, 
  Compass, 
  Clock, 
  Heart, 
  Sparkles,
  ArrowRight,
  Check,
  X,
  Headphones,
  FileText,
  Archive,
  LogIn
} from 'lucide-react';

const CampusPayPalButton = ({ plan }) => {
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [immediateAccess, setImmediateAccess] = useState(false);

  const createSubscription = async (data, actions) => {
    try {
      const res = await api.post('/campus/subscribe', {
        plan,
        base_url: window.location.origin,
      });
      if (res.data.approve_url) {
        // For redirect flow
        window.location.href = res.data.approve_url;
        return res.data.subscription_id;
      }
      return res.data.subscription_id;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al crear la suscripción';
      toast.error(msg);
      throw err;
    }
  };

  const onApprove = async (data) => {
    try {
      await api.post('/campus/subscribe/activate', {
        subscription_id: data.subscriptionID,
        plan,
      });
      toast.success('¡Suscripción activada! Bienvenida al Campus');
      navigate('/campus');
    } catch {
      toast.error('Error al activar. Contacta con soporte.');
    }
  };

  const accepted = termsAccepted && immediateAccess;

  return (
    <div data-testid="paypal-subscribe-container" className="space-y-4">
      {/* Legal acceptance block */}
      <div className="p-4 rounded-xl bg-white/90 border border-[#e8d9a8] space-y-3 text-left">
        <p className="text-xs text-[#1e3a5f]/80 leading-relaxed">
          <strong>Información precontractual:</strong> contratas una suscripción
          {plan === 'campus_annual' ? ' anual (12 meses)' : ' mensual'} al Campus de Interioridad con{' '}
          <strong>renovación automática</strong> mediante PayPal. Puedes cancelar la renovación en cualquier momento desde tu área privada o escribiendo a hola@herminiovazquez.com antes de la siguiente fecha de cobro.
        </p>
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id={`campus-terms-${plan}`}
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#c9a227] cursor-pointer"
            data-testid={`campus-terms-${plan}`}
          />
          <label htmlFor={`campus-terms-${plan}`} className="text-xs text-[#1e3a5f]/80 leading-relaxed cursor-pointer">
            He leído y acepto los{' '}
            <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Términos y condiciones</a>
            {' '}y la{' '}
            <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Política de privacidad</a>, incluyendo los cargos periódicos de renovación automática.
          </label>
        </div>
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id={`campus-immediate-${plan}`}
            checked={immediateAccess}
            onChange={(e) => setImmediateAccess(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#c9a227] cursor-pointer"
            data-testid={`campus-immediate-${plan}`}
          />
          <label htmlFor={`campus-immediate-${plan}`} className="text-xs text-[#1e3a5f]/80 leading-relaxed cursor-pointer">
            Solicito expresamente el acceso inmediato al contenido digital tras el pago y reconozco que, al iniciar el suministro, pierdo el derecho legal de desistimiento (art. 103.m TRLGDCU). La escuela ofrece una garantía comercial de 14 días sobre la primera cuota.
          </label>
        </div>
      </div>

      <div className={!accepted ? 'pointer-events-none opacity-50' : ''}>
        <PayPalButtons
          style={{
            layout: 'vertical',
            color: 'gold',
            shape: 'pill',
            label: 'subscribe',
            height: 45,
          }}
          createSubscription={createSubscription}
          onApprove={onApprove}
          onError={() => toast.error('Error con PayPal. Inténtalo de nuevo.')}
          onCancel={() => toast.info('Has cancelado el pago.')}
        />
      </div>
      {!accepted && (
        <p className="text-xs text-[#b45a30] text-center font-medium">
          Acepta las condiciones para activar el pago.
        </p>
      )}
    </div>
  );
};

const CampusInterioridad = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('campus_monthly');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [activating, setActivating] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(true);

  useEffect(() => {
    checkUserStatus();
    // Read public config to know if sales are open
    api.get('/campus/config')
      .then(r => setSubscriptionsEnabled(!!r.data?.subscriptions_enabled))
      .catch(() => setSubscriptionsEnabled(false));
  }, []);

  // Handle PayPal return
  useEffect(() => {
    const paypalStatus = searchParams.get('paypal');
    const subscriptionId = searchParams.get('subscription_id');
    const plan = searchParams.get('plan');
    if (paypalStatus === 'success' && subscriptionId) {
      activateSubscription(subscriptionId, plan);
    } else if (paypalStatus === 'cancelled') {
      toast.info('Has cancelado el proceso de suscripción');
    }
  }, [searchParams]);

  const checkUserStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setIsLoggedIn(false); setCheckingAccess(false); return; }
      setIsLoggedIn(true);
      const res = await api.get('/campus/status');
      if (res.data.has_access) setHasAccess(true);
    } catch {
      setIsLoggedIn(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const activateSubscription = async (subscriptionId, plan) => {
    setActivating(true);
    try {
      await api.post('/campus/subscribe/activate', {
        subscription_id: subscriptionId,
        plan: plan || 'campus_monthly'
      });
      toast.success('¡Suscripción activada! Bienvenida al Campus');
      setHasAccess(true);
    } catch (err) {
      toast.error('Error al activar la suscripción. Contacta con soporte.');
    } finally {
      setActivating(false);
    }
  };

  // Subscription panel with PayPal buttons
  const renderSubscriptionPanel = () => {
    if (checkingAccess) {
      return (
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-[#1e3a5f]/10 text-center">
          <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      );
    }

    if (activating) {
      return (
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-[#1e3a5f]/10 text-center">
          <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#1e3a5f]/70">Activando tu suscripción...</p>
        </div>
      );
    }

    if (hasAccess) {
      return (
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-[#c9a227]/30 text-center">
          <div className="w-16 h-16 bg-[#c9a227]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-[#c9a227]" />
          </div>
          <h3 className="text-2xl font-heading text-[#1e3a5f] mb-4">
            Ya tienes acceso al Campus
          </h3>
          <p className="text-[#1e3a5f]/70 mb-6">
            Tu suscripción está activa. Puedes entrar al Campus de Interioridad.
          </p>
          <Button
            onClick={() => navigate('/campus')}
            className="bg-[#c9a227] hover:bg-[#b8922a] text-white px-8 py-6 text-lg w-full"
            data-testid="campus-enter-btn"
          >
            Entrar al Campus
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      );
    }

    if (!isLoggedIn) {
      return (
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-[#1e3a5f]/10 text-center">
          <LogIn className="w-10 h-10 text-[#c9a227] mx-auto mb-4" />
          <h3 className="text-2xl font-heading text-[#1e3a5f] mb-4">
            Inicia sesión para suscribirte
          </h3>
          <p className="text-[#1e3a5f]/60 text-sm mb-6">
            Necesitas una cuenta para acceder al Campus de Interioridad.
          </p>
          <Button
            onClick={() => navigate('/login?redirect=/campus-interioridad')}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white px-8 py-5 text-base w-full mb-3"
            data-testid="campus-login-btn"
          >
            Iniciar sesión
          </Button>
          <Button
            onClick={() => navigate('/register?redirect=/campus-interioridad')}
            variant="outline"
            className="border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227]/10 px-8 py-5 text-base w-full"
            data-testid="campus-register-btn"
          >
            Crear cuenta
          </Button>
        </div>
      );
    }

    // Sales closed — admin has not opened subscriptions yet.
    if (!subscriptionsEnabled) {
      return (
        <div
          className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-[#c9a227]/30 text-center"
          data-testid="campus-sales-closed"
        >
          <div className="w-16 h-16 bg-[#c9a227]/15 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-[#c9a227]" />
          </div>
          <h3 className="text-2xl font-heading text-[#1e3a5f] mb-3">
            Las puertas aún no están abiertas
          </h3>
          <p className="text-[#1e3a5f]/70 leading-relaxed mb-6 max-w-sm mx-auto">
            El Campus de Interioridad abrirá sus suscripciones próximamente. Si quieres,
            te avisamos personalmente cuando estén disponibles.
          </p>
          <Button
            onClick={() => scrollToSection('lista-espera')}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white px-8 py-5 text-base w-full"
            data-testid="campus-notify-me-btn"
          >
            Avísame cuando se abra
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      );
    }

    // Logged in but no subscription - show PayPal buttons
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-[#1e3a5f]/10">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-heading text-[#1e3a5f] mb-2">
            Suscríbete al Campus
          </h3>
          <p className="text-[#1e3a5f]/60 text-sm">
            Elige tu plan y accede al Campus hoy
          </p>
        </div>

        {/* Plan selector */}
        <div className="flex gap-2 mb-6 bg-[#1e3a5f]/5 rounded-xl p-1">
          <button
            onClick={() => setSelectedPlan('campus_monthly')}
            className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${
              selectedPlan === 'campus_monthly'
                ? 'bg-white text-[#1e3a5f] shadow-sm'
                : 'text-[#1e3a5f]/60 hover:text-[#1e3a5f]'
            }`}
            data-testid="plan-monthly-btn"
          >
            22€/mes
          </button>
          <button
            onClick={() => setSelectedPlan('campus_annual')}
            className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all relative ${
              selectedPlan === 'campus_annual'
                ? 'bg-white text-[#1e3a5f] shadow-sm'
                : 'text-[#1e3a5f]/60 hover:text-[#1e3a5f]'
            }`}
            data-testid="plan-annual-btn"
          >
            220€/año
            <span className="absolute -top-2 -right-1 text-[9px] bg-[#c9a227] text-white px-1.5 py-0.5 rounded-full">-17%</span>
          </button>
        </div>

        <div className="mb-4 text-center">
          <p className="text-[#1e3a5f]/50 text-xs">
            {selectedPlan === 'campus_monthly' 
              ? 'Renovación mensual automática. Cancela cuando quieras.'
              : 'Ahorra el equivalente a 2 meses. Renovación anual.'}
          </p>
        </div>

        {/* PayPal Buttons */}
        <PayPalScriptProvider
          options={{
            'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID,
            vault: true,
            intent: 'subscription',
            currency: 'EUR',
            locale: 'es_ES',
          }}
        >
          <CampusPayPalButton plan={selectedPlan} />
        </PayPalScriptProvider>

        <p className="text-[#1e3a5f]/40 text-[10px] text-center mt-4">
          Pago seguro procesado por PayPal. Sin permanencia mínima.
        </p>
      </div>
    );
  };

  // SVG Logo component - pure golden arch with orb, truly transparent
  const CampusLogo = ({ className = "", size = "w-24 h-24" }) => (
    <svg 
      viewBox="0 0 100 100" 
      className={`${size} ${className}`}
      fill="none"
    >
      {/* Outer glow */}
      <defs>
        <radialGradient id="orbGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#c9a227" stopOpacity="0.8"/>
          <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="archGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e8d5a3"/>
          <stop offset="50%" stopColor="#c9a227"/>
          <stop offset="100%" stopColor="#b8922a"/>
        </linearGradient>
      </defs>
      
      {/* Glow behind orb */}
      <circle cx="50" cy="55" r="25" fill="url(#orbGlow)"/>
      
      {/* Arch */}
      <path 
        d="M20 85 L20 45 Q20 15 50 15 Q80 15 80 45 L80 85" 
        stroke="url(#archGradient)" 
        strokeWidth="4" 
        strokeLinecap="round"
        fill="none"
      />
      
      {/* Inner arch line */}
      <path 
        d="M30 85 L30 48 Q30 25 50 25 Q70 25 70 48 L70 85" 
        stroke="url(#archGradient)" 
        strokeWidth="2" 
        strokeLinecap="round"
        fill="none"
        opacity="0.5"
      />
      
      {/* Central orb */}
      <circle cx="50" cy="55" r="12" fill="#c9a227"/>
      <circle cx="50" cy="55" r="8" fill="#e8d5a3"/>
      <circle cx="47" cy="52" r="3" fill="white" opacity="0.6"/>
    </svg>
  );

  return (
    <div className="bg-gradient-to-b from-[#0d1b2a] via-[#1e3a5f] to-[#0d1b2a] min-h-screen">
      
      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1776877464/landing_campus_vehe93.png"
            alt="Portal sagrado"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1b2a]/80 via-[#1e3a5f]/60 to-[#0d1b2a]/90" />
        </div>

        {/* Floating particles effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-[#c9a227]/30 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              {/* Logo */}
              <div className="flex justify-center lg:justify-start mb-8">
                <CampusLogo size="w-24 h-24 lg:w-32 lg:h-32" />
              </div>

              {/* Status badge */}
              <div className="inline-block px-4 py-2 bg-[#c9a227]/20 backdrop-blur-sm rounded-full mb-6">
                <span className="text-[#c9a227] text-sm font-medium tracking-wide">
                  Abierto en muy pocos días! · Suscripción mensual o anual
                </span>
              </div>

              {/* Main title */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white leading-tight mb-6">
                Campus de
                <span className="block text-[#c9a227]">Interioridad</span>
              </h1>

              {/* Subtitle */}
              <p className="text-xl lg:text-2xl text-white/80 font-light leading-relaxed mb-4">
                Un espacio continuo de orientación, lectura consciente y práctica interna
              </p>
              
              <p className="text-lg text-white/60 mb-8">
                para sostenerte en el tiempo sin exigencia ni procesos intensivos.
              </p>

              {/* CTA for mobile */}
              <div className="lg:hidden">
                <Button 
                  onClick={() => scrollToSection('lista-espera')}
                  className="bg-[#c9a227] hover:bg-[#b8922a] text-white px-8 py-6 text-lg"
                >
                  Suscribirme al Campus
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right: Waitlist Form (desktop) */}
            <div className="hidden lg:block" id="lista-espera">
              {renderSubscriptionPanel()}
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== THE LONGING / ANHELO SECTION ========== */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#0d1b2a] to-[#1e3a5f]/50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="space-y-8">
            <p className="text-xl lg:text-2xl text-white/90 leading-relaxed font-light">
              Hay días en los que sientes que te has alejado de ti.
            </p>
            <p className="text-xl lg:text-2xl text-white/80 leading-relaxed font-light">
              No sabes exactamente cuándo ocurrió, pero lo notas.
            </p>
            <p className="text-xl lg:text-2xl text-white/70 leading-relaxed font-light">
              El ruido volvió. La dispersión volvió. 
              <span className="block mt-2">Y esa sensación de estar sosteniendo mucho… sin que nadie te sostenga a ti.</span>
            </p>
            
            <div className="pt-8">
              <div className="w-16 h-[1px] bg-[#c9a227]/50 mx-auto mb-8" />
              <p className="text-lg text-[#c9a227] font-medium">
                Este espacio existe para eso: para volver.
              </p>
              <p className="text-white/60 mt-4">
                Sin grandes procesos. Sin exigencias. Solo un lugar donde recordarte quién eres.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHAT IT IS ========== */}
      <section className="py-24 lg:py-32 bg-[#f8f6f0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image/Symbol */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-full bg-gradient-to-br from-[#1e3a5f]/10 to-[#c9a227]/10 flex items-center justify-center">
                  <CampusLogo size="w-40 h-40 lg:w-48 lg:h-48" />
                </div>
                {/* Decorative rings */}
                <div className="absolute inset-0 border border-[#c9a227]/20 rounded-full animate-pulse" style={{ transform: 'scale(1.1)' }} />
                <div className="absolute inset-0 border border-[#1e3a5f]/10 rounded-full" style={{ transform: 'scale(1.2)' }} />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <div className="w-12 h-[2px] bg-[#c9a227]" />
              
              <h2 className="text-3xl lg:text-4xl font-heading text-[#1e3a5f]">
                ¿Qué es el Campus de Interioridad?
              </h2>
              
              <p className="text-lg text-[#1e3a5f]/80 leading-relaxed">
                El Campus de Interioridad es un <strong>espacio mensual de acompañamiento suave</strong> para personas que ya han iniciado un camino de consciencia y sienten la necesidad de ordenarse, sostenerse y habitarse en el día a día.
              </p>
              
              <div className="space-y-3 pt-4">
                <div className="flex items-center gap-3 text-[#1e3a5f]/70">
                  <X className="w-5 h-5 text-[#c9a227]" />
                  <span>No es una formación.</span>
                </div>
                <div className="flex items-center gap-3 text-[#1e3a5f]/70">
                  <X className="w-5 h-5 text-[#c9a227]" />
                  <span>No es una terapia.</span>
                </div>
                <div className="flex items-center gap-3 text-[#1e3a5f]/70">
                  <X className="w-5 h-5 text-[#c9a227]" />
                  <span>No es un proceso de transformación rápida.</span>
                </div>
              </div>

              <div className="bg-[#1e3a5f]/5 rounded-2xl p-6 mt-6">
                <p className="text-[#1e3a5f] font-medium">
                  Es un marco estable que te acompaña a lo largo del tiempo para que no te pierdas, no te disperses y no abandones tu eje interno.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOR WHOM ========== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-8" />
            <h2 className="text-3xl lg:text-4xl font-heading text-[#1e3a5f]">
              ¿Para quién es este espacio?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* For whom it IS */}
            <div className="bg-gradient-to-br from-[#1e3a5f]/5 to-[#c9a227]/5 rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#c9a227]/20 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#c9a227]" />
                </div>
                <h3 className="text-xl font-heading text-[#1e3a5f]">Este Campus es para ti si...</h3>
              </div>
              
              <ul className="space-y-4">
                {[
                  'Ya has hecho trabajo personal o espiritual',
                  'Sientes que necesitas sostén, no más información',
                  'Te interesa comprender las energías del tiempo sin dejarte arrastrar',
                  'Valoras el texto, la reflexión y la práctica consciente',
                  'Buscas un ritmo amable, sin presión ni exigencias'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Sparkles className="w-4 h-4 text-[#c9a227] mt-1 flex-shrink-0" />
                    <span className="text-[#1e3a5f]/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* For whom it is NOT */}
            <div className="bg-[#f8f6f0] rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center">
                  <X className="w-5 h-5 text-[#1e3a5f]/60" />
                </div>
                <h3 className="text-xl font-heading text-[#1e3a5f]">Este espacio NO es para ti si...</h3>
              </div>
              
              <ul className="space-y-4">
                {[
                  'Buscas terapia o acompañamiento individual',
                  'Necesitas respuestas personalizadas o intervención directa',
                  'Esperas resultados rápidos o procesos intensivos',
                  'Quieres una comunidad muy activa o emocional',
                  'Prefieres un formato de formación tradicional'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#1e3a5f]/40 mt-1 flex-shrink-0">—</span>
                    <span className="text-[#1e3a5f]/60">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center text-[#c9a227] font-medium mt-12 text-lg">
            El Campus de Interioridad no empuja procesos: los acompaña.
          </p>
        </div>
      </section>

      {/* ========== WHAT'S INCLUDED ========== */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#1e3a5f] to-[#0d1b2a]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-8" />
            <h2 className="text-3xl lg:text-4xl font-heading text-white mb-4">
              ¿Qué incluye el Campus?
            </h2>
            <p className="text-white/60">Cada mes tendrás acceso a:</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: BookOpen,
                title: '1 lectura consciente del mes',
                description: 'Un texto profundo y claro para comprender el clima energético del momento y orientarte internamente.'
              },
              {
                icon: Compass,
                title: '2 ajustes quincenales de orientación',
                description: 'Textos breves para recalibrar el eje, revisar el foco y volver a ti.'
              },
              {
                icon: FileText,
                title: '1 herramienta práctica escrita',
                description: 'Sencilla y aplicable: preguntas, enfoques o micro-prácticas para el día a día.'
              },
              {
                icon: Headphones,
                title: '2 meditaciones guiadas (en audio y/o video) al mes',
                description: 'Meditaciones de sostén y presencia para acompañar el proceso quincenal.'
              },
              {
                icon: Archive,
                title: 'Acceso al archivo completo',
                description: 'Todo el contenido queda disponible para que lo uses a tu ritmo, sin prisas ni acumulación.'
              },
              {
                icon: Clock,
                title: 'Tu propio ritmo',
                description: 'No necesitas "ponerte al día". Puedes estar más activo un mes y menos otro.'
              }
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border border-white/10 hover:border-[#c9a227]/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a227]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading text-white mb-2">{item.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-white/80 text-lg">
              El Campus no exige constancia: <span className="text-[#c9a227]">acompaña</span>.
            </p>
            <p className="text-white/50 mt-2">
              Este espacio está diseñado para regular, no para acelerar.
            </p>
          </div>
        </div>
      </section>

      {/* ========== PHILOSOPHY ========== */}
      <section className="py-24 lg:py-32 bg-[#f8f6f0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-8" />
          <h2 className="text-3xl lg:text-4xl font-heading text-[#1e3a5f] mb-12">
            Filosofía del Campus
          </h2>

          <blockquote className="text-2xl lg:text-3xl font-heading text-[#1e3a5f]/80 leading-relaxed mb-8">
            "No todo proceso necesita ser empujado.
            <span className="block text-[#c9a227] mt-2">Algunos solo necesitan un espacio donde sostenerse."</span>
          </blockquote>

          <p className="text-xl text-[#1e3a5f]/70 leading-relaxed">
            Aquí no vienes a hacer más.
            <span className="block mt-2 font-medium text-[#1e3a5f]">Vienes a volver.</span>
          </p>
        </div>
      </section>

      {/* ========== PERSONAL LETTER ========== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-br from-[#1e3a5f]/5 to-[#c9a227]/5 rounded-3xl p-8 lg:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full bg-[#1e3a5f] flex items-center justify-center">
                <Heart className="w-7 h-7 text-[#c9a227]" />
              </div>
              <div>
                <h2 className="text-2xl font-heading text-[#1e3a5f]">¿Por qué creé este espacio?</h2>
                <p className="text-[#1e3a5f]/60">Una carta personal</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-[#1e3a5f]/80 leading-relaxed space-y-6">
              <p>
                Llevo años acompañando procesos de transformación. He visto personas conectarse, escucharse, alinearse, atravesar capas profundas de sí mismas. Y también he visto lo que ocurre después.
              </p>
              
              <p>
                Después de un proceso intensivo, muchas personas se quedan sin marco. Sin un lugar al que volver. Y poco a poco, sin darse cuenta, se van alejando de su eje. No porque no quieran sostenerse, sino porque la vida empuja, el ruido vuelve, y el contacto con uno mismo se va diluyendo.
              </p>
              
              <p className="text-[#1e3a5f] font-medium">
                El Campus de Interioridad nace de esa observación.
              </p>
              
              <p>
                No es un espacio para transformarte. Es un espacio para no perderte.
              </p>
              
              <p>
                Un lugar sencillo, sin exigencias, donde puedas volver a ti cuando lo necesites. Donde haya una lectura esperándote, una meditación, una palabra que te recuerde quién eres cuando te has olvidado.
              </p>
              
              <p>
                Yo también necesité un espacio así. Un lugar que no me pidiera más esfuerzo, sino que simplemente me sostuviera. Y no lo encontré. Por eso lo creo ahora.
              </p>
              
              <p>
                El Campus no promete grandes cambios. Promete algo más humilde y más profundo: <strong className="text-[#c9a227]">estar ahí</strong>.
              </p>
              
              <p className="text-right italic text-[#1e3a5f]/60 pt-4">
                — Herminio
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PRICING PREVIEW ========== */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#0d1b2a] to-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-8" />
            <h2 className="text-3xl lg:text-4xl font-heading text-white mb-4">
              Acceso y precio
            </h2>
            <p className="text-white/60">(Elige el plan que mejor te convenga)</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monthly */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <h3 className="text-xl font-heading text-white mb-2">Membresía mensual</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-4xl font-heading text-[#c9a227]">22€</span>
                <span className="text-white/60">/ mes</span>
              </div>
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#c9a227]" />
                  Acceso inmediato a todo el contenido
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#c9a227]" />
                  Renovación mensual automática
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#c9a227]" />
                  Sin permanencia
                </li>
              </ul>
            </div>

            {/* Annual */}
            <div className="bg-gradient-to-br from-[#c9a227]/20 to-[#c9a227]/5 rounded-3xl p-8 border border-[#c9a227]/30 relative">
              <div className="absolute -top-3 right-6 px-3 py-1 bg-[#c9a227] text-white text-xs font-medium rounded-full">
                Ahorra 2 meses
              </div>
              <h3 className="text-xl font-heading text-white mb-2">Acceso anual</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-4xl font-heading text-[#c9a227]">220€</span>
                <span className="text-white/60">/ año</span>
              </div>
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#c9a227]" />
                  Acceso completo durante 12 meses
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#c9a227]" />
                  Ideal para sostén continuado
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#c9a227]" />
                  Ahorro frente a mensual
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 lg:py-32 bg-[#f8f6f0]" id="lista-espera-mobile">
        <div className="max-w-2xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-8" />
            <h2 className="text-3xl lg:text-4xl font-heading text-[#1e3a5f] mb-6">
              Para terminar
            </h2>
            <p className="text-xl text-[#1e3a5f]/80 leading-relaxed mb-4">
              El Campus de Interioridad no es un lugar al que venir a buscar respuestas.
            </p>
            <p className="text-xl text-[#c9a227] font-medium">
              Es un lugar al que volver cuando te has alejado de ti.
            </p>
          </div>

          {/* Mobile form */}
          <div className="lg:hidden">
            {renderSubscriptionPanel()}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block text-center">
            <Button 
              onClick={() => scrollToSection('lista-espera')}
              className="bg-[#c9a227] hover:bg-[#b8922a] text-white px-10 py-6 text-lg"
            >
              Suscribirme al Campus
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-[#1e3a5f]/50 mt-4 text-sm">
              22€/mes o 220€/año · Sin permanencia
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default CampusInterioridad;
